<template>
  <layout class="payment-list-page-container">
    <div class="w-full flex adm-container sm:flex-col lg:flex-row">
      <div class="search-input sm:w-full lg:w-1/2">
        <div class="w-full h-full flex items-center justify-center">
          <div class="w-full">
            <suggestion-input
              :list="driverList"
              :lookup-fields="['name', 'number']"
              accessor="name"
              placeholder="Search drivers by name or mobile number"
              icon-class="fa-search"
              icon-position="left"
              @autocomplete-item-set="onAutoCompleteItemSelected"
              @autocomplete-cleared="onAutoCompleteItemCleared"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4 sm:my-4 lg:my-0">
        <pt-datepicker
          :value="date"
          :range="weekRange"
          :week-range="weekRange"
          :show-next-month-dates="weekRange"
          :show-prev-month-dates="weekRange"
          :close-on-range-select="true"
          class="flex flex-col flex-1 sm:pl-0 lg:pl-4"
          @selected="setDate"
        >
          <div
            slot="afterDayPicker"
            class="p-4"
          >
            <pt-radio-group
              v-model="selectedDateRangeType"
              :options="dateSelectionType"
              name="dateSelection"
            />
          </div>
        </pt-datepicker>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4 sm:mb-8 lg:mb-0">
        <download-csv
          :download-url="csvUrl"
          file-name="Payments List"
        />
      </div>
    </div>
    <div class="w-full flex flex-col adm-container">
      <div class="table-loader-container relative">
        <pt-list>
          <div class="font-bold bg-transparent sm:hidden lg:flex sm:p-0 lg:p-4 sm:border-b-0 lg:border-b">
            <div class="w-1/6">Driver</div>
            <div class="w-5/6 flex lg:text-right">
              <div class="flex-1">Trip Fare</div>
              <div class="flex-1">Net Fare</div>
              <div class="flex-1">Quest</div>
              <div class="flex-1">Misc Payment</div>
              <div class="flex-1">Cash Collected</div>
              <div class="flex-1">Digital Payment</div>
              <div class="flex-1">Net Payout</div>
            </div>
          </div>
          <pt-list-item
            v-for="(paymentRow, paymentIndex) in paymentList"
            :key="paymentIndex + 1"
          >
            <div class="flex flex-1 sm:flex-col lg:flex-row">
              <div class="sm:flex sm:w-full lg:w-1/6 sm:py-1 lg:py-0">
                <span class="font-bold lg:hidden sm:mr-2">Partner-Driver: </span>
                <router-link
                  :to="`/driver/${paymentRow.driver_id}`"
                  class="flex no-underline cursor-pointer text-black hover:text-grey-darker items-center font-bold">
                  {{ paymentRow.name }}
                </router-link>
              </div>
              <div class="w-5/6 flex sm:flex-col lg:flex-row sm:w-full lg:w-5/6">
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Trip Fare:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.trip_fare) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Net Earning:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.net_earning) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Quest:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.quest) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Misc Payment:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.misc_payment) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Cash Collected:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.cash_collected) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Digital Payment:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.collected_via_wallet) }}</span>
                </div>
                <div class="flex flex-1 sm:py-1 lg:py-0">
                  <span class="font-bold mr-2 lg:hidden">Net Payout:</span>
                  <span class="lg:w-full lg:text-right">{{ CurrencyConverter(paymentRow.net_payout) }}</span>
                </div>
              </div>
            </div>
          </pt-list-item>
        </pt-list>

        <table-placeholder
          :is-syncing="isLoadingListData"
          :data-length="paymentList.length"
          loader-class="text-black"
        />
      </div>
      <pt-pagination
        :total-rows="totalRows"
        :current-page="currentPage"
        :page-size="pageSize"
        @page-changed="fetchPaymentList"
      />
    </div>
  </layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PtDropdown, PtDropdownItem } from '@pathao/ui-components/esm/Dropdown';
import { PtRadioGroup, PtRadio } from '@pathao/ui-components/esm/FormComponents';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtDatepicker } from '@pathao/ui-components/esm/Datepicker';
import { PtLoader } from '@pathao/ui-components/esm/Loader';
import { PtList, PtListItem } from '@pathao/ui-components/esm/List';
import extendObj from 'lodash/extend';
import Layout from '@layouts/main';
import Config from '@config';
import PaginationConfig from '@config/pagination';
import TablePlaceholder from '@components/custom/TablePlaceholder';
import SuggestionInput from '@components/custom/SuggestionInput';
import DownloadCsv from '@components/custom/DownloadCsv';
import makeRequest from '@utils/apiRequest';
import RunningWeek from '@utils/running-week';
import SelectedDates from '@utils/selected-dates';
import CurrencyConverter from '@plugins/CurrencyConverter';

const PAYMENTS_LIST_BASE_URL = `${Config.api.baseURL}api/partners/payments/list`;

export default {
  name: 'Payments',
  page: {
    title: 'Payments',
  },
  components: { SuggestionInput, PtDropdown, PtDropdownItem, PtPagination, Layout, PtDatepicker, PtRadioGroup, PtRadio, PtLoader, TablePlaceholder, DownloadCsv, PtList, PtListItem },
  mixins: [ CurrencyConverter ],
  data() {
    return {
      date: RunningWeek(),
      selectedDateRangeType: 'weekly',
      dateSelectionType: [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
      ],
      filteredDriverId: null,
      driverList: [],
      paymentList: [],
      totalRows: 0,
      currentPage: PaginationConfig.CURRENT_PAGE,
      pageSize: PaginationConfig.PAGE_SIZE,
      isLoadingListData: false,
    };
  },
  computed: {
    ...mapGetters({
      driverList: 'drivers/drivers',
    }),
    weekRange() {
      return this.selectedDateRangeType === 'weekly';
    },
    csvUrl() {
      const baseUrl = `${PAYMENTS_LIST_BASE_URL}/export`;
      const paramList = [];

      if (this.date) {
        const selectedDates = SelectedDates(this.date);

        if (selectedDates.filterByDate) {
          paramList.push(`filterByDate=${selectedDates['filterByDate']}`);
        } else {
          paramList.push(`filterByStartDate=${selectedDates['filterByStartDate']}`);
          paramList.push(`filterByEndDate=${selectedDates['filterByEndDate']}`);
        }
      }

      if (this.filteredDriverId) {
        paramList.push(`filterByDriverId=${this.filteredDriverId}`);
      }

      return paramList.length ? `${baseUrl}?${paramList.join('&')}` : baseUrl;
    },
  },
  watch: {
    selectedDateRangeType() {
      this.date = null;
    },
  },
  mounted() {
    this.fetchPaymentList();
    this.fetchDriverList();
  },
  methods: {
    ...mapActions({
      fetchDriverList: 'drivers/fetchDrivers',
    }),
    fetchPaymentList(page = PaginationConfig.CURRENT_PAGE, per_page = PaginationConfig.PAGE_SIZE) {
      const selectedDates = SelectedDates(this.date);

      let paymentListParams = {
        page,
        per_page,
        ...selectedDates,
      };

      if (this.filteredDriverId) {
        paymentListParams = extendObj({}, paymentListParams, { filterByDriverId: this.filteredDriverId });
      }

      const paymentListMeta = {
        url: PAYMENTS_LIST_BASE_URL,
        params: paymentListParams,
      };

      this.isLoadingListData = true;
      this.paymentList = [];

      makeRequest(paymentListMeta)
        .then((listResponse) => {
          this.paymentList = listResponse.data.data;
          this.totalRows = listResponse.data.pagination.total;
          this.currentPage = listResponse.data.pagination.current_page;
          this.pageSize = listResponse.data.pagination.per_page;

          this.isLoadingListData = false;
        })
        .catch((errorResponse) => {
          this.$notification.error('Error in getting payment list');
          console.error(errorResponse);

          this.isLoadingListData = false;
        });
    },
    setDate(date) {
      this.date = date;
      this.fetchPaymentList();
    },
    onAutoCompleteItemSelected(driver) {
      this.filteredDriverId = driver.id;
      this.fetchPaymentList();
    },
    onAutoCompleteItemCleared() {
      this.filteredDriverId = null;
      this.fetchPaymentList();
    },
  },
};
</script>
